/**
 * IT Admin → User lookup → "Access at a glance" (21 Oct, Rusty: "really
 * enhanced so they can see all that info at a glance").
 *
 * A one-line summary (how many systems show access, which ones are not
 * connected yet), then one card per system: 365FO, Bluestar, Power BI, CRM,
 * Folders, SharePoint, Entra groups, licences (software and anything
 * unclassified when found). Each entry says how the person has it and where
 * it was read, with the request and its date. A system no source can answer
 * says "Not connected yet" and which source will answer it — never an empty
 * list that reads as "has nothing". Hidden while the switch is off.
 */

import { Alert, Badge, Card, Typography } from "@norican/react";

import { ErrorState, LoadingRows, SectionCard, useDateFormatter } from "@/components/common";
import { ReferenceEntries, SourceLines } from "@/features/access/components/reference-access";
import { useT } from "@/i18n";
import type { ProfilePerson, ReferenceAccessSystem } from "@/lib/api";

import { useItAdminPersonAccess } from "./api";

const STATE_TONE = {
  ok: "success",
  empty: "neutral",
  not_connected: "neutral",
  error: "danger",
} as const;

export function AccessGlance({
  employeeId,
  person,
}: {
  employeeId: string;
  person: ProfilePerson;
}) {
  const { t } = useT("tickets");
  const { formatDateTime } = useDateFormatter();
  const query = useItAdminPersonAccess(employeeId);
  if (query.isPending) {
    return (
      <SectionCard title={t("itAdmin.glance.title")}>
        <LoadingRows rows={3} />
      </SectionCard>
    );
  }
  if (query.isError) {
    return (
      <SectionCard title={t("itAdmin.glance.title")}>
        <ErrorState variant="inline" error={query.error} onRetry={() => void query.refetch()} />
      </SectionCard>
    );
  }
  const data = query.data;
  if (data.enabled === false) {
    return null;
  }
  // 28 Oct (Rusty): a system not connected yet is left out, not listed as such.
  const systems = (data.systems ?? []).filter((system) => system.state !== "not_connected");
  const withAccess = systems.filter((system) => system.state === "ok");
  return (
    <SectionCard
      title={t("itAdmin.glance.title")}
      description={
        data.read_at
          ? t("itAdmin.glance.readAt", { when: formatDateTime(data.read_at) })
          : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <PersonSummary person={person} />
        {data.complete === false ? (
          <Alert tone="warning">{t("itAdmin.glance.partial")}</Alert>
        ) : null}
        <Typography as="p" variant="body-sm">
          {withAccess.length === 0
            ? t("itAdmin.glance.summaryNone")
            : t("itAdmin.glance.summary", {
                count: withAccess.length,
                list: withAccess.map((system) => systemName(t, system.key)).join(", "),
              })}
        </Typography>
        <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3">
          {systems.map((system) => (
            <li key={system.key} className="min-w-0">
              <SystemGlanceCard system={system} />
            </li>
          ))}
        </ul>
      </div>
    </SectionCard>
  );
}

type Translate = ReturnType<typeof useT>["t"];

function systemName(t: Translate, key: string): string {
  return t(`access.reference.system.${key}` as never);
}

function SystemGlanceCard({ system }: { system: ReferenceAccessSystem }) {
  const { t } = useT("tickets");
  const items = system.items ?? [];
  return (
    <Card
      variant="outline"
      padding="sm"
      className="flex h-full flex-col gap-2"
      aria-label={systemName(t, system.key)}
    >
      <span className="flex flex-wrap items-center gap-2">
        <Typography as="h3" variant="label">
          {systemName(t, system.key)}
        </Typography>
        <Badge tone={STATE_TONE[system.state]} variant="subtle" size="xs">
          {system.state === "ok"
            ? t("itAdmin.glance.count", { count: items.length })
            : t(`itAdmin.glance.state.${system.state}`)}
        </Badge>
      </span>
      {items.length > 0 ? (
        <ReferenceEntries items={items} linkTickets />
      ) : system.state === "empty" ? (
        <Typography as="p" variant="body-sm" tone="muted">
          {t("itAdmin.glance.emptyText")}
        </Typography>
      ) : null}
      <SourceLines sources={system.sources ?? []} />
    </Card>
  );
}

/** Who at a glance: title, department, site, manager, user ID, and any leaver flag. */
function PersonSummary({ person }: { person: ProfilePerson }) {
  const { t } = useT("tickets");
  const facts = [
    person.title,
    person.department,
    person.location,
    person.manager ? t("itAdmin.glance.manager", { name: person.manager }) : null,
    person.customer_id ? t("itAdmin.glance.userId", { id: person.customer_id }) : null,
  ].filter((part): part is string => typeof part === "string" && part.trim() !== "");
  const flags = [
    person.disabled ? "disabled" : null,
    person.terminated ? "terminated" : null,
    person.entra_deleted ? "entraDeleted" : null,
  ].filter((flag): flag is "disabled" | "terminated" | "entraDeleted" => flag !== null);
  return (
    <div className="flex flex-col gap-1">
      <span className="flex flex-wrap items-center gap-2">
        <Typography as="span" variant="heading-6">
          {person.name}
        </Typography>
        {person.status ? (
          <Badge tone="neutral" variant="subtle" size="xs">
            {person.status}
          </Badge>
        ) : null}
        {flags.map((flag) => (
          <Badge key={flag} tone="warning" variant="subtle" size="xs">
            {t(`itAdmin.person.leaverFlag.${flag}` as never)}
          </Badge>
        ))}
      </span>
      {facts.length > 0 ? (
        <Typography as="span" variant="body-sm" tone="muted" className="break-words">
          {facts.join(" · ")}
        </Typography>
      ) : null}
    </div>
  );
}
